/**
 * OGSM SSO bridge launcher.
 *
 * Compass owns Clerk; OGSM trusts a single-use session JWT POSTed to
 * `<OGSM>/auth/sso`. This module fetches the JWT via Clerk's getToken()
 * and form-submits it to OGSM, which verifies the token, establishes a
 * Django session, and 302s to `next`.
 */

/** Subset of Clerk's `getToken` options we forward. */
interface GetTokenOptions {
  skipCache?: boolean
  template?: string
}

export interface PostToOgsmSsoBridgeOptions {
  /**
   * Resolves to a Clerk session JWT, or null/undefined when unavailable.
   * Matches the shape Clerk's `useAuth().getToken` exposes so callers can
   * pass it directly. The bridge always invokes it with
   * `{ skipCache: true }` so each launch gets a fresh JWT body — OGSM's
   * `/auth/sso` replay-protects on the SHA-256 hash of the token, and a
   * cached token would be rejected as a replay on rapid re-launches.
   */
  getToken: (options?: GetTokenOptions) => Promise<string | null | undefined>
  /** OGSM origin (e.g. `https://ogsm-uat.greybullstewardship.com`). */
  ogsmBaseUrl: string | undefined
  /** Path on OGSM to land on after bridge. Defaults to `/ogsm/`. */
  next?: string
  /** Open OGSM in a new tab instead of replacing the current window. */
  openInNewTab?: boolean
}

export type OgsmSsoBridgeFailureReason =
  | 'not_configured'
  | 'no_token'
  | 'invalid_url'

export class OgsmSsoBridgeError extends Error {
  readonly reason: OgsmSsoBridgeFailureReason

  constructor(reason: OgsmSsoBridgeFailureReason) {
    super(`OGSM SSO bridge failed: ${reason}`)
    this.name = 'OgsmSsoBridgeError'
    this.reason = reason
  }
}

export async function postToOgsmSsoBridge({
  getToken,
  ogsmBaseUrl,
  next = '/ogsm/',
  openInNewTab = false,
}: PostToOgsmSsoBridgeOptions): Promise<void> {
  const trimmedBase = ogsmBaseUrl?.trim()
  if (!trimmedBase) {
    throw new OgsmSsoBridgeError('not_configured')
  }

  let actionUrl: string
  try {
    actionUrl = new URL('/auth/sso', trimmedBase).toString()
  } catch {
    throw new OgsmSsoBridgeError('invalid_url')
  }

  // Pre-open the destination tab synchronously while the click's user
  // activation is still alive. `await getToken()` below relinquishes the
  // gesture; if we deferred `window.open` until after the await, Safari
  // and Firefox would block the popup. Submitting a form whose `target`
  // matches an already-open tab's name just navigates that existing tab,
  // which the popup blocker does not police.
  let popupWindow: Window | null = null
  let formTarget = ''
  if (openInNewTab) {
    popupWindow = window.open('about:blank', '_blank')
    if (popupWindow) {
      formTarget = `ogsm_sso_bridge_${Date.now()}`
      popupWindow.name = formTarget
    }
  }

  let token: string | null | undefined
  try {
    token = await getToken({ skipCache: true })
  } catch (error) {
    popupWindow?.close()
    throw error
  }
  if (!token) {
    popupWindow?.close()
    throw new OgsmSsoBridgeError('no_token')
  }

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = actionUrl
  form.style.display = 'none'
  if (formTarget) {
    form.target = formTarget
  } else if (openInNewTab) {
    // Popup blocker prevented us from opening a tab synchronously. Falling
    // back to same-tab navigation is better than losing the launch — the
    // user still ends up on OGSM logged in.
    form.target = '_self'
  }

  appendHiddenInput(form, 'token', token)
  appendHiddenInput(form, 'next', next)

  document.body.appendChild(form)
  form.submit()
  // Give the browser a microtask to initiate the submission before we
  // detach the form node.
  setTimeout(() => {
    form.parentNode?.removeChild(form)
  }, 0)
}

function appendHiddenInput(form: HTMLFormElement, name: string, value: string) {
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = name
  input.value = value
  form.appendChild(input)
}

/**
 * Normalize an OGSM deep-link `next` query param into a local OGSM path that
 * can be safely posted to `<OGSM>/auth/sso`.
 *
 * Accepted shapes:
 *   - Absolute URL whose origin matches `ogsmBaseUrl` -> returns
 *     pathname + search + hash.
 *   - Path-relative URL beginning with `/` and not `//` (so that
 *     `//evil.example/...` is rejected) -> returned unchanged.
 *
 * Rejected shapes (returns `null`):
 *   - Missing or empty `next`.
 *   - Absolute URL whose origin does not match `ogsmBaseUrl`.
 *   - Protocol-relative URL (`//evil.example/...`).
 *   - `javascript:` / `data:` / `vbscript:` URLs.
 *   - Malformed URL string.
 *   - `ogsmBaseUrl` itself empty or unparseable.
 */
export function resolveOgsmNext(
  searchNext: string | null | undefined,
  ogsmBaseUrl: string | null | undefined
): string | null {
  if (!searchNext) {
    return null
  }

  const trimmedBase = ogsmBaseUrl?.trim()
  if (!trimmedBase) {
    return null
  }

  let ogsmOrigin: string
  try {
    ogsmOrigin = new URL(trimmedBase).origin
  } catch {
    return null
  }

  const candidate = searchNext.trim()
  if (!candidate) {
    return null
  }

  // Protocol-relative URLs (`//evil.example/...`) and dangerous schemes are
  // never allowed regardless of subsequent parsing.
  if (candidate.startsWith('//')) {
    return null
  }
  const lowered = candidate.toLowerCase()
  if (
    lowered.startsWith('javascript:') ||
    lowered.startsWith('data:') ||
    lowered.startsWith('vbscript:')
  ) {
    return null
  }

  // Local OGSM path: accept absolute paths so manually-typed
  // `/auth/redirect?next=/ogsm/sessions/?foo=bar` works for ops/dev.
  if (candidate.startsWith('/')) {
    return candidate
  }

  // Absolute URL: only accept when origin matches the configured OGSM base.
  try {
    const parsed = new URL(candidate)
    if (parsed.origin !== ogsmOrigin) {
      return null
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/'
  } catch {
    return null
  }
}
